import React from "react";

import type { Header, RowData } from "@tanstack/react-table-v9";

import { gridSpacing } from "@utils";

import { columnLayout, NO_DRAG, pinEdges } from "./layout";
import type { ColumnDragState, DropPlacement } from "./layout";
import ToolTip from "../../../ToolTip/ToolTip";
import { canReorderColumn, moveColumn } from "../../columnUtils";
import type { TableV2Features } from "../../features";
import { getColumnAlign } from "../../formatters";
import type { TableV2Instance } from "../../hooks/useTableV2";
import {
  CellBox,
  CellText,
  HeaderMenuSlot,
  Resizer,
  SortLabel,
} from "../../styles";
import ColumnHeaderMenu from "../ColumnHeaderMenu";

/** Sortable, draggable header with its menu and resize handle */
const HeaderCell = <TData extends RowData>({
  table,
  header,
  showMenu,
  allowDrag,
  drag,
  setDrag,
  onFit,
  endInset = 0,
  fixedWidth = false,
}: {
  table: TableV2Instance<TData>;
  header: Header<TableV2Features, TData, unknown>;
  fixedWidth?: boolean;
  /** Extra right padding so header actions don't cover the label */
  endInset?: number;
  showMenu: boolean;
  allowDrag: boolean;
  drag: ColumnDragState;
  setDrag: React.Dispatch<React.SetStateAction<ColumnDragState>>;
  onFit: (columnId: string) => void;
}) => {
  const { column } = header;
  const meta = column.columnDef.meta;
  const sorted = column.getIsSorted();
  const canSort = !header.isPlaceholder && column.getCanSort();
  const isLeaf = header.subHeaders.length === 0;
  const draggable =
    allowDrag && isLeaf && !header.isPlaceholder && canReorderColumn(column);
  const isDropTarget =
    drag.target?.id === column.id && drag.draggingId !== column.id;

  const dragHandlers = draggable
    ? {
        onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
          if (!drag.draggingId || drag.draggingId === column.id) return;
          // Only reorder within the same pinned region; across regions the move wouldn't show
          if (
            table.getColumn(drag.draggingId)?.getIsPinned() !==
            column.getIsPinned()
          )
            return;
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          const placement: DropPlacement =
            event.clientX < rect.left + rect.width / 2 ? "before" : "after";
          if (
            drag.target?.id !== column.id ||
            drag.target.placement !== placement
          ) {
            setDrag((current) => {
              return { ...current, target: { id: column.id, placement } };
            });
          }
        },
        onDrop: (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          if (drag.draggingId && drag.target) {
            moveColumn(
              table,
              drag.draggingId,
              column.id,
              drag.target.placement,
            );
          }
          setDrag(NO_DRAG);
        },
      }
    : {};

  return (
    <CellBox
      $isHeader
      $wrap={meta?.wrap}
      role="columnheader"
      data-column-id={isLeaf ? column.id : undefined}
      $align={isLeaf ? getColumnAlign(meta) : "center"}
      $pinned={column.getIsPinned()}
      $dropIndicator={isDropTarget ? drag.target?.placement : undefined}
      $dragging={drag.draggingId === column.id}
      {...pinEdges(column)}
      {...dragHandlers}
      data-sort={sorted || undefined}
      style={{
        ...columnLayout(
          column,
          header.getSize(),
          !isLeaf && !header.isPlaceholder,
          fixedWidth,
        ),
        paddingRight:
          endInset && isLeaf && getColumnAlign(meta) !== "left"
            ? endInset + gridSpacing[3]
            : undefined,
      }}
    >
      {header.isPlaceholder ? null : (
        <SortLabel
          data-fit-row
          $canSort={canSort}
          draggable={draggable}
          onClick={canSort ? column.getToggleSortingHandler() : undefined}
          tabIndex={canSort ? 0 : undefined}
          onKeyDown={
            canSort
              ? (event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  column.getToggleSortingHandler()?.(event);
                }
              : undefined
          }
          onDragStart={
            draggable
              ? (event) => {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", column.id);
                  setDrag({ draggingId: column.id, target: null });
                }
              : undefined
          }
          onDragEnd={draggable ? () => setDrag(NO_DRAG) : undefined}
        >
          {meta?.overflow ? (
            <table.FlexRender header={header} />
          ) : (
            <CellText>
              <table.FlexRender header={header} />
            </CellText>
          )}
          {sorted && (
            <i
              className={
                sorted === "desc"
                  ? "fa-solid fa-arrow-down"
                  : "fa-solid fa-arrow-up"
              }
            />
          )}
          {meta?.info && (
            <ToolTip text={meta.info} direction="top">
              <i className="fa-regular fa-circle-info" />
            </ToolTip>
          )}
        </SortLabel>
      )}
      {showMenu && isLeaf && !header.isPlaceholder && (
        <HeaderMenuSlot
          data-column-menu
          $side={getColumnAlign(meta) === "right" ? "left" : "right"}
        >
          <ColumnHeaderMenu
            column={column}
            isResized={table.state.columnSizing[column.id] !== undefined}
            onFit={() => onFit(column.id)}
          />
        </HeaderMenuSlot>
      )}
      {isLeaf && column.getCanResize() && (
        <Resizer
          data-testid={`resize-${column.id}`}
          $active={column.getIsResizing()}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onDoubleClick={() => onFit(column.id)}
          onClick={(event) => event.stopPropagation()}
        />
      )}
    </CellBox>
  );
};

export default HeaderCell;
